import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmationConfig, FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseConfirmationDialogComponent } from '@fuse/services/confirmation/dialog/dialog.component';
import { StringUtils } from '@hmax-order/shared/string';
import { TranslocoService } from '@jsverse/transloco';

interface ICfgConfirmation {
  message: string;
  title?: string;
  /** Se true, `message` é exibida sem passar por translate (ex.: HTML já montado no componente). */
  skipTranslateMessage?: boolean;
  confirmCaption?: string;
  confirmColor?: 'primary' | 'warn' | 'accent';
  cancelCaption?: string;
  iconName?: string;
  iconColor?: 'basic' | 'info' | 'warn' | 'success';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  confirmMethod?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cancelMethod?: any;
}

type IOptionConfirmation = Omit<ICfgConfirmation, 'iconName' | 'iconColor' | 'confirmColor'>;

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private fuseConfirmationService = inject(FuseConfirmationService);
  private translocoService = inject(TranslocoService);

  /**
   * Abre confirmação com botão principal na core padrão
   */
  confirm(opt: IOptionConfirmation) {
    const optExtra: ICfgConfirmation = opt;
    optExtra.iconName = 'heroicons_outline:question-mark-circle';
    optExtra.iconColor = 'basic';
    this.open(optExtra);
  }

  /**
   * Abre confirmação com botão principal em cor de alerta
   */
  confirmWarn(opt: IOptionConfirmation) {
    const optExtra: ICfgConfirmation = opt;
    optExtra.iconName = 'heroicons_outline:question-mark-circle';
    optExtra.iconColor = 'basic';
    optExtra.confirmColor = 'warn';
    this.open(optExtra);
  }

  /**
   * Abre diálogo para exibir informações ao usuário, sem necessidade de confirmação
   */
  info(message: string, title?: string) {
    const opt: ICfgConfirmation = {
      title,
      message,
      iconName: 'mat_outline:warning_amber',
      iconColor: 'info',
      confirmCaption: 'understood',
      cancelCaption: '',
    };
    this.open(opt);
  }

  /**
   * Abre diálogo para exibir mensagem de erro ao usuário, sem necessidade de confirmação
   */
  error(message: string, title?: string) {
    const opt: ICfgConfirmation = {
      title,
      message,
      iconName: 'mat_outline:warning_amber',
      iconColor: 'warn',
      confirmColor: 'warn',
      confirmCaption: 'understood',
      cancelCaption: '',
    };
    this.open(opt);
  }

  /**
   * Abre a confirmação conforme as configurações informadas
   */
  private open(opt: IOptionConfirmation) {
    const msgConfig = this.getConfirmationConfig(opt);
    // Open the confirmation and save the reference
    const dialogRef = this.fuseConfirmationService.open(msgConfig);
    this.subscribeAfterClosed(dialogRef, opt.confirmMethod, opt.cancelMethod);
  }

  /**
   * Subscrevese como observer no evento afterClosed da referência de diálogo passada como parâmetro
   * @param dialogRef Referência da "tela" de diálogo
   * @param confirmMethod Método a ser executado se o usuário clicar no botão de confirmação
   * @param cancelMethod Método a ser acionado se o usuário clicar no botão de cancelamento
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private subscribeAfterClosed(dialogRef: MatDialogRef<FuseConfirmationDialogComponent, any>, confirmMethod?: any, cancelMethod?: any) {
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result === 'confirmed' && confirmMethod) {
        return confirmMethod();
      }
      if (result === 'cancelled' && cancelMethod) {
        return cancelMethod();
      }
    });
  }

  /**
   * Retorna objeto de configuração para o diálogo de confirmação,
   * atribuindo valores padrão para as configurações não informadas
   */
  private getConfirmationConfig(opt: ICfgConfirmation): FuseConfirmationConfig {
    const confirmCaption = opt.confirmCaption ?? 'confirm';
    const cancelCaption = opt.cancelCaption ?? 'cancel';
    const iconName = opt.iconName ?? 'mat_outline:warning_amber';
    const iconColor = opt.iconColor ?? 'basic';
    const confirmColor = opt.confirmColor ?? 'primary';

    const title = StringUtils.capitalize(opt?.title ? this.translocoService.translate(opt.title || '') : '');
    const message = opt?.message ? (opt.skipTranslateMessage ? opt.message : this.translocoService.translate(opt.message)) : '';
    const labelConfirmCaption = StringUtils.capitalize(this.translocoService.translate(confirmCaption));
    const labelCancelCaption = StringUtils.capitalize(this.translocoService.translate(cancelCaption));

    const cfg: FuseConfirmationConfig = {
      title,
      message,
      icon: {
        show: !!iconName,
        name: iconName,
        color: iconColor,
      },
      actions: {
        confirm: {
          show: !!confirmCaption,
          label: labelConfirmCaption,
          color: confirmColor,
        },
        cancel: {
          show: !!cancelCaption,
          label: labelCancelCaption,
        },
      },
      dismissible: false,
    };

    return cfg;
  }
}
